/* Verbatim port of WS's board (§7) rendering: renderSkeleton (~3772-3775),
 * renderBoard (~3928-3978), toggleDensity (~3980-3986), rowHtml
 * (~4020-4047), wireBoard (~4049-4065), statusMenu (~4067-4131),
 * closeMenu (~4133-4138 — kept as a Workspace instance method in
 * state/workspace.ts, not re-exported here, since nothing outside WS ever
 * calls it).
 *
 * Every function takes the Workspace instance (`ws`) as an explicit first
 * parameter instead of closing over `this` (F1/F2 porting rule "a"),
 * mirrored back onto WS.renderBoard(el)/WS.rowHtml(t,cls)/etc. by
 * state/workspace.ts so every original `WS.xxx(...)` call site — the
 * inline onclick markup below included — keeps resolving through the real
 * global `WS` object (state/workspace.ts's `declare global` block).
 *
 * ── F3c (wave C) lit-html conversion — per the wiki.ts conventions block ──
 * `esc()` dropped throughout. `rowHtml` now returns a TemplateResult (was
 * `string`) — its ONLY consumer anywhere in the codebase is `renderBoard`'s
 * own `grp` closure (grepped: `WS.rowHtml` has zero external callers), so
 * the signature change is fully contained; state/workspace.ts's wrapper
 * method updates its declared return type to match.
 *
 * DEVIATION (wireBoard removed): every `[data-act]` button `wireBoard` used
 * to wire via a post-render `querySelectorAll(...).forEach()` pass is now
 * wired directly in `rowHtml`'s own template via `@click` (a shared
 * `actClick` helper below, re-resolving the task from `ws.tasks` by id at
 * CLICK time rather than trusting the render-time closure's task object —
 * same discipline F3a's conflicts.ts `chipFor` and F3b's exec.ts
 * `wsactClick` already established for this exact class of button). Since
 * `wireBoard` had zero external callers either (grepped: `WS.wireBoard` is
 * never invoked outside `renderBoard`'s own trailing call), it is REMOVED
 * (implementation + Workspace method) rather than left as dead code —
 * same "remove rather than leave inert" precedent main.ts's window-shim
 * checklist already established for dead entries.
 *
 * DEVIATION (toggleDensity's targeted micro-update): the original swaps
 * just the clicked button's own label (`btn.innerHTML = ...`) plus a
 * `classList.toggle('compact', ...)` on the board container — deliberately
 * NOT a full board re-render, so any expanded `.wsexp` row (with an
 * in-progress note draft) survives a density toggle. Lit's `render()`
 * doesn't clear a container's pre-existing content the way `innerHTML=`
 * does (see the F3c report), so mixing "content written by the OUTER
 * renderBoard() template" with "a later independent render() call on that
 * same button" would double-stack. Fixed by rendering the button’s label
 * EMPTY in the outer template (a `data-density` hook, no bindings inside
 * it) and seeding it via its own dedicated `render()` call right after the
 * main render — the same "each sub-region gets its own render() call"
 * granularity convention #2 already uses for cards/tbodies, just applied
 * to a single button. `toggleDensity` itself then targets that SAME
 * independent part on every later click — no stacking, and the targeted-
 * update behavior (no full re-render) is preserved exactly.
 *
 * WS.ICON is now a real `svg\`\`` TemplateResult dictionary (state/
 * workspace.ts) — every `${ws.ICON.x}` site below is a plain lit child
 * binding (the transitional `unsafeHTML` wrapping this file shipped with
 * is gone; see the WS.ICON conversion commit).
 *
 * F4 item 3 (store decoupling): unlike views/actions.ts, ws/strip.ts,
 * ws/notes.ts, views/exec.ts and views/conflicts.ts, this file deliberately
 * does NOT subscribe to wsChangeStore (state/wsChange.ts) itself.
 * `renderBoard`'s only caller anywhere in the codebase (grepped) is
 * views/actions.ts's `renderActions`, which ALREADY subscribes and already
 * owns the pre-existing (unrelated to this task) three-way branch that
 * decides whether `#actionsBody` gets the live board, the probing
 * skeleton, or the offline derived table. A second, independent
 * subscription in this file would either never fire usefully (actions.ts's
 * subscription already re-renders `#actionsBody` on the same signal) or,
 * worse, race it for ownership of that element. `statusMenu`'s
 * `ws.repaintAll()` calls (below) are unchanged — they still notify via
 * that same store, just through the Workspace method rather than directly.
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { rawUrl } from '../api/client.ts';
import { AUTH, type AuthUser } from '../lib/auth.ts';
import { cap } from '../lib/format.ts';
import { activeCapacity, capacityLabel, heldCapacities, resolveLanding, setActiveCapacity } from '../lib/personas.ts';
import { payloadStore } from '../state/payload.ts';
import { exportActionsCsv, renderActions } from '../views/actions.ts';
import { labelChips } from './labels.ts';
import { checklistBadge } from './checklist.ts';
import { savedFilterControl } from './savedFilters.ts';
import { openUsersPanel } from './adminUsers.ts';
import { openTaskPanel } from './taskDetail.ts';
import type { Workspace } from '../state/workspace.ts';
import type { WsBoardFilter, WsTask } from '../types/workspace.ts';

export function renderSkeleton(el: HTMLElement): void {
  render(html`<div class="finding" style="color:var(--muted)">Loading the shared board…</div>
    ${Array.from({ length: 6 }, () => html`<div class="wsskel-row"></div>`)}`, el);
}

/* Task-Management Phase 1 / T5: pure filter-state → URL query builder. Param
   names match the server's GET /clients/{slug}/tasks contract exactly (q /
   status / assignee / priority / due). An all-empty filter yields '', which
   state/workspace.ts's boardUrl() turns back into the bare (full-list) path —
   so the fetch is byte-identical to pre-T5 when nothing is filtered. Kept
   side-effect-free and exported for board-filter.test.ts. */
export function buildFilterQuery(f: WsBoardFilter): string {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set('q', f.q.trim());
  if (f.status) p.set('status', f.status);
  if (f.assignee) p.set('assignee', f.assignee);
  if (f.priority) p.set('priority', f.priority);
  if (f.due) p.set('due', f.due);
  if (f.label) p.set('label', f.label); // P2/L: a label id, AND-joined server-side
  return p.toString();
}

/* The board filter bar (T5) — a second toolbar row: a full-text box (focusable
   with `/`, see main.ts) plus status/assignee/priority/due selects, all wired
   to ws.applyFilter() which round-trips to the server's search_tasks. Rendered
   only on the live board (renderBoard runs only when ws.available). Tokens
   only — no raw hex/px. */
export function filterBar(ws: Workspace): TemplateResult {
  const f = ws._filter;
  const active = ws.hasServerFilter();
  const DUE: Array<[WsBoardFilter['due'], string]> = [
    ['', 'Any due'], ['overdue', 'Overdue'], ['today', 'Due today'], ['week', 'Due this week'],
  ];
  return html`
    <div class="wsfilter" role="search">
      <input id="wsFilterQ" class="wsfilter-q" type="search" .value=${f.q}
        placeholder="Search tasks — title, detail, notes  ( / )" aria-label="Search tasks"
        @input=${(ev: Event) => ws.applyFilter({ q: (ev.target as HTMLInputElement).value }, false)}
        @keydown=${(ev: KeyboardEvent) => { if (ev.key === 'Enter') { ev.preventDefault(); if (ws._filterTimer) { clearTimeout(ws._filterTimer); ws._filterTimer = null; } ws.applyFilter({ q: (ev.target as HTMLInputElement).value }); } }}>
      <select class="wsfilter-sel" aria-label="Filter by status" .value=${f.status}
        @change=${(ev: Event) => ws.applyFilter({ status: (ev.target as HTMLSelectElement).value })}>
        <option value="">All statuses</option>
        ${Object.keys(ws.STATUS_LABEL).map((s) => html`<option value="${s}">${ws.STATUS_FULL[s] || s}</option>`)}
      </select>
      <select class="wsfilter-sel" aria-label="Filter by assignee" .value=${f.assignee}
        @change=${(ev: Event) => ws.applyFilter({ assignee: (ev.target as HTMLSelectElement).value })}>
        <option value="">Anyone</option>
        ${ws.roster().map((n) => html`<option value="${n}">${n}</option>`)}
      </select>
      <select class="wsfilter-sel" aria-label="Filter by priority" .value=${f.priority}
        @change=${(ev: Event) => ws.applyFilter({ priority: (ev.target as HTMLSelectElement).value })}>
        <option value="">Any priority</option>
        <option value="high">High</option>
        <option value="medium">Medium</option>
        <option value="low">Low</option>
      </select>
      <select class="wsfilter-sel" aria-label="Filter by due date" .value=${f.due}
        @change=${(ev: Event) => ws.applyFilter({ due: (ev.target as HTMLSelectElement).value as WsBoardFilter['due'] })}>
        ${DUE.map(([v, label]) => html`<option value="${v}">${label}</option>`)}
      </select>
      ${ws.labels.length ? html`<select class="wsfilter-sel" aria-label="Filter by label" .value=${f.label}
        @change=${(ev: Event) => ws.applyFilter({ label: (ev.target as HTMLSelectElement).value })}>
        <option value="">Any label</option>
        ${ws.labels.map((l) => html`<option value="${l.id}">${l.name}</option>`)}
      </select>` : nothing}
      ${active ? html`<button class="wsmini wsfilter-clear" @click=${() => ws.clearFilter()} title="clear all server filters">✕ clear</button>` : nothing}
      ${savedFilterControl(ws)}
    </div>`;
}

/* Task-Management Phase 1 / T6: the bulk-action bar. Rendered only when the
   board multi-select (ws._selected) is non-empty. Every action routes through
   ws.bulkApply/bulkAssign → POST /tasks/bulk, which loops the SAME guarded
   store fns the single-task path uses — so illegal moves are reported per id
   (D9), never forced, and Verified/Regressed stay unreachable here too. Tokens
   only — no raw hex. */
export function selectionBar(ws: Workspace): TemplateResult | typeof nothing {
  const sel = ws.selectedTasks();
  if (!sel.length) return nothing;
  const shownN = ws.filterProg(ws.tasks).length;
  return html`
    <div class="wsselbar" role="toolbar" aria-label="bulk actions on selected tasks">
      <span class="wsselcount"><b>${sel.length}</b> selected</span>
      <button class="wsmini" aria-haspopup="menu"
        @click=${(ev: MouseEvent) => { ev.stopPropagation(); bulkStatusMenu(ws, ev.currentTarget as HTMLElement); }}>${ws.ICON.check}Set status ▾</button>
      <button class="wsmini"
        @click=${() => ws.ask({ title: `Assign ${sel.length} task${sel.length === 1 ? '' : 's'} to`, value: ws.author() || '', placeholder: 'name', options: ws.roster() }).then((v) => { if (v !== null) ws.bulkAssign(v); })}>${ws.ICON.user}Assign</button>
      <button class="wsmini"
        @click=${() => ws.ask({ title: `Due date for ${sel.length} task${sel.length === 1 ? '' : 's'}`, type: 'date', value: '' }).then((v) => { if (v !== null) ws.bulkApply({ due_date: v }); })}>${ws.ICON.cal}Due</button>
      <button class="wsmini" title="mark as part of the agreed client program"
        @click=${() => ws.bulkApply({ in_program: true })}>${ws.ICON.starFill}Program</button>
      ${sel.length < shownN ? html`<button class="wsmini" @click=${() => ws.selectAllShown()}>Select all ${shownN}</button>` : nothing}
      <button class="wsmini wsselclear" style="margin-left:auto" @click=${() => ws.clearSelection()}>✕ Clear</button>
    </div>`;
}

/* The bulk status menu: only the transitions that are legal for at least one
   selected task (a union across their current statuses — Verified/Regressed
   are never PATCH targets, so they can't appear). A move that's illegal for
   SOME of the selection still applies to the rest and reports the others
   (D9). wont-do prompts once for a shared reason, exactly like the single-task
   statusMenu. Same detached-host + outside-click + Escape wiring as
   statusMenu, through ws._menu/_menuOut/closeMenu.

   Q-03 CTO condition F1: `attested` is EXCLUDED from the bulk union — attesting
   is dialog-only (per-task, mandatory per-task evidence note via statusMenu).
   Without this exclusion the item rendered but fell to the no-reason branch and
   409'd every id ("Updated 0"). A shared note across N tasks is a product call
   we are NOT making (CTO F4 ruling) — so the item simply never appears here. */
const BULK_EXCLUDED = ['attested'];
export function bulkStatusMenu(ws: Workspace, btn: HTMLElement): void {
  ws.closeMenu();
  const sel = ws.selectedTasks();
  const legal = [...new Set(sel.flatMap((t) => ws.transitions[t.status] || []))]
    .filter((s) => !BULK_EXCLUDED.includes(s));
  const m = document.createElement('div');
  m.className = 'wsmenu';
  m.setAttribute('role', 'menu');
  render(html`${legal.length
      ? legal.map((s) => html`<button role="menuitem" tabindex="-1" data-to="${s}">${ws.STATUS_FULL[s] || s}</button>`)
      : html`<div class="note">No move is legal across this whole selection.</div>`}
    <div class="note">Applied per task — an illegal move for some is reported, not forced. Verified/Regressed come from evidence, never here; Attested needs its own per-task evidence note (each task's status menu).</div>`, m);
  document.body.appendChild(m);
  const r = btn.getBoundingClientRect();
  m.style.left = Math.min(r.left, innerWidth - 240) + 'px';
  // Flip the menu ABOVE its button when there isn't room below (review #8): the
  // CRM rail-foot identity button sits at the viewport bottom, so a
  // below-anchored menu put Sign out off-screen. A no-op when room exists (every
  // top-of-viewport toolbar menu keeps opening downward). offsetHeight is
  // readable now that the menu is in the DOM.
  const mh = m.offsetHeight;
  const roomBelow = r.bottom + 4 + mh <= innerHeight;
  m.style.top = (roomBelow ? r.bottom + scrollY + 4 : Math.max(scrollY + 4, r.top + scrollY - mh - 4)) + 'px';
  ws._menu = m; ws._menuOwner = btn;
  const items = [...m.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]')];
  items.forEach((x, i) => {
    x.onclick = async () => {
      const to = x.dataset.to!;
      if (to === 'wont-do') {
        ws.closeMenu();
        const reason = await ws.ask({ title: `Why won't these ${sel.length} be done?`, multiline: true,
          hint: 'Required — saved on each task so the next run marks them known instead of re-flagging.', ok: "Won't do" });
        if (!reason) return;
        ws.bulkApply({ status: to, reason });
      } else {
        ws.closeMenu(true);
        ws.bulkApply({ status: to });
      }
    };
    x.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); (items[i + 1] || items[0])?.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (items[i - 1] || items[items.length - 1])?.focus(); }
      else if (e.key === 'Escape') { e.preventDefault(); ws.closeMenu(true); }
    });
  });
  if (items[0]) items[0].focus();
  ws._menuOut = (ev: MouseEvent) => { if (!m.contains(ev.target as Node) && ev.target !== btn) ws.closeMenu(); };
  setTimeout(() => document.addEventListener('click', ws._menuOut!), 0);
}

/* shared click handler for every `data-act` button rowHtml renders —
   re-resolves the task from ws.tasks by id at click time (see the module
   doc comment above). */
function actClick(ws: Workspace, ev: MouseEvent, tid: number, act: string): void {
  ev.stopPropagation();
  const b = ev.currentTarget as HTMLElement;
  const row = b.closest('.wsrow') as HTMLElement;
  const t = ws.tasks.find((x) => x.id === tid);
  if (!t) return;
  if (act === 'status') statusMenu(ws, b, t);
  else if (act === 'assign') ws.ask({ title: 'Assign to', value: t.assignee || ws.author() || '', placeholder: 'pick a teammate', options: ws.roster(), hint: 'Pick a teammate from the roster — an assignee must be a registered user.' }).then((v) => {
    // C7 (phantom-assignee fix): assignment must resolve to a REAL user id. A
    // typed name that doesn't match the server roster is REJECTED with a clear
    // message — it must never silently become a phantom owner (the old free-text
    // fallback that broke My Work / notifications / watchers). Cancel (null) or
    // an empty entry is a no-op.
    if (v === null) return;
    const u = ws.resolveUser(v);
    if (!u) { ws.toast(`"${v}" isn't on the roster — pick a registered teammate (no free-typed owners).`, 'warn'); return; }
    ws.setField(t, 'assignee_user_id', u.id);
  });
  else if (act === 'due') ws.ask({ title: 'Due date', type: 'date', value: t.due_date || '' }).then((v) => ws.setField(t, 'due_date', v));
  else if (act === 'program') ws.setField(t, 'in_program', !t.in_program);
  else if (act === 'prio') ws.setField(t, 'priority',
    t.priority === 'high' ? 'medium' : t.priority === 'medium' ? 'low' : 'high');
  else if (act === 'recheck') ws.recheck(t, b as HTMLButtonElement);
  else if (act === 'expand') ws.toggleExpand(row, t);
  else if (act === 'open') openTaskPanel(ws, t);
}

/* The ONE within-lane / within-group sort, shared by the list board and the
   kanban view (Design gate — one comparator, no drift): overdue floats first,
   then priority, then category. Exported so kanban.ts reuses it verbatim rather
   than duplicating the logic. */
export function laneSort(ws: Workspace): (a: WsTask, b: WsTask) => number {
  const PR: Record<string, number> = { high: 0, medium: 1, low: 2 };
  return (a, b) =>
    Number(ws.overdueDays(b) > 0) - Number(ws.overdueDays(a) > 0)
    || (PR[a.priority] ?? 2) - (PR[b.priority] ?? 2)
    || (a.category || '').localeCompare(b.category || '');
}

/* The consolidated Export ▾ button (Design gate — ONE export affordance in BOTH
   the list and board toolbars, replacing the three separate buttons). Opens
   exportMenu below. */
export function exportButton(ws: Workspace): TemplateResult {
  return html`<button class="wsmini" aria-haspopup="menu" title="export the board"
    @click=${(ev: MouseEvent) => { ev.stopPropagation(); exportMenu(ws, ev.currentTarget as HTMLElement); }}>${ws.ICON.down}Export<span class="wscaret" aria-hidden="true">▾</span></button>`;
}

/* The Export ▾ dropdown: CSV · Completed→Claude · Client progress report. Same
   detached-host + outside-click + Escape wiring as identityMenu, through the
   shared ws._menu/_menuOut/closeMenu infrastructure. */
export function exportMenu(ws: Workspace, btn: HTMLElement): void {
  ws.closeMenu();
  const progressReportUrl = rawUrl(`/clients/${ws.clientSlug}/progress-report`);
  const m = document.createElement('div');
  m.className = 'wsmenu';
  m.setAttribute('role', 'menu');
  render(html`
    <button role="menuitem" tabindex="-1" @click=${() => { ws.closeMenu(true); exportActionsCsv(payloadStore.get()); }}>${ws.ICON.down}Export CSV</button>
    <button role="menuitem" tabindex="-1" title="completed items with full history + evidence, as JSON — paste into Claude to draft the client's work-completed report" @click=${() => { ws.closeMenu(true); ws.exportCompleted(); }}>${ws.ICON.check}Completed → Claude (JSON)</button>
    <a role="menuitem" tabindex="-1" style="text-decoration:none" href="${progressReportUrl}" target="_blank" @click=${() => ws.closeMenu()}>${ws.ICON.docLines}Client progress report</a>`, m);
  document.body.appendChild(m);
  const r = btn.getBoundingClientRect();
  m.style.left = Math.min(r.left, innerWidth - 240) + 'px';
  // Flip the menu ABOVE its button when there isn't room below (review #8): the
  // CRM rail-foot identity button sits at the viewport bottom, so a
  // below-anchored menu put Sign out off-screen. A no-op when room exists (every
  // top-of-viewport toolbar menu keeps opening downward). offsetHeight is
  // readable now that the menu is in the DOM.
  const mh = m.offsetHeight;
  const roomBelow = r.bottom + 4 + mh <= innerHeight;
  m.style.top = (roomBelow ? r.bottom + scrollY + 4 : Math.max(scrollY + 4, r.top + scrollY - mh - 4)) + 'px';
  ws._menu = m; ws._menuOwner = btn;
  const items = [...m.querySelectorAll<HTMLElement>('[role="menuitem"]')];
  items.forEach((x, i) => {
    x.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); (items[i + 1] || items[0])?.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (items[i - 1] || items[items.length - 1])?.focus(); }
      else if (e.key === 'Escape') { e.preventDefault(); ws.closeMenu(true); }
    });
  });
  if (items[0]) items[0].focus();
  ws._menuOut = (ev: MouseEvent) => { if (!m.contains(ev.target as Node) && ev.target !== btn) ws.closeMenu(); };
  setTimeout(() => document.addEventListener('click', ws._menuOut!), 0);
}

export function renderBoard(ws: Workspace, el: HTMLElement): void {
  const T = ws.tasks;
  // repeat() keyed by task id (here and in the closed <details> tail below):
  // a re-render that reorders tasks MOVES each row's DOM node instead of
  // rewriting row contents in place, so per-row DOM state (focus, an open
  // .wsexp expander, a checkbox mid-click) stays with ITS task across
  // repaintAll()/refresh() reorders. kanban.ts's lane card lists do the same.
  const grp = (label: string, rows: WsTask[], cls: string, note?: string): TemplateResult | typeof nothing => rows.length ? html`
    <div class="wsgroup">
      <h4>${label}<span class="cnt">${rows.length}</span>${note ? html`<span style="font-weight:400;text-transform:none;letter-spacing:0">${note}</span>` : nothing}</h4>
      ${repeat(rows, (t) => t.id, (t) => rowHtml(ws, t, cls))}
    </div>` : nothing;
  // overdue floats above everything inside its group (round-3: a 36-day
  // -late task rendered as a neutral gray date nobody would ever see) — the
  // ONE shared comparator, reused verbatim by the kanban view (no drift).
  const by = laneSort(ws);
  const regressed = T.filter((t) => t.status === 'regressed').sort(by);
  const pending = T.filter((t) => t.status === 'implemented').sort(by);
  const waiting = T.filter((t) => /^waiting/.test(t.status)).sort((a, b) => ws.stateAge(b) - ws.stateAge(a));
  const active = T.filter((t) => t.status === 'open' || t.status === 'in-progress').sort(by);
  const closed = T.filter((t) => t.status === 'verified' || t.status === 'wont-do');
  // Q-03: attested is its OWN group + its OWN count — never folded into the
  // verified count (Design condition 4). A distinct terminal-good reading.
  const attested = T.filter((t) => t.status === 'attested');
  const verifiedN = T.filter((t) => t.status === 'verified').length;
  const attestedN = attested.length;
  const stalledN = waiting.filter((t) => ws.stateAge(t) > 14).length;
  const overdueN = T.filter((t) => ws.overdueDays(t) > 0).length;
  const shown = ws.filterProg(regressed).length + ws.filterProg(pending).length
    + ws.filterProg(waiting).length + ws.filterProg(active).length + ws.filterProg(closed).length
    + ws.filterProg(attested).length;
  const filtered = shown !== T.length;

  render(html`
    <div class="finding">${filtered ? html`showing <b>${shown} of ${T.length}</b> items (filtered)` : `${T.length} items on the shared board`} — <b>${active.filter((t) => t.priority === 'high').length} high-priority open</b>${overdueN ? html`, <span style="color:var(--neg-strong)">${overdueN} overdue</span>` : nothing}${verifiedN ? `, ${verifiedN} verified` : ''}${attestedN ? `, ${attestedN} human-attested` : ''}${pending.length ? `, ${pending.length} awaiting verification` : ''}${stalledN ? html`, <span style="color:var(--amber-tx)">${stalledN} stalled waiting on client</span>` : nothing}.</div>
    <div class="why"><b>This board is shared:</b> every status, owner and note is saved on the server with who/when — the whole pod sees the same state. "Verified" is earned, never clicked: it requires a recheck probe or the next research run confirming the fix. Statuses survive new runs; a verified fix that breaks again comes back as <b>Regressed</b>.</div>
    <div style="display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap">
      ${viewToggle(ws)}
      <button class="wsmini" @click=${() => ws.addCustomTask()}>${ws.ICON.plus}Add task</button>
      ${exportButton(ws)}
      <button class="wsmini" data-density @click=${(ev: MouseEvent) => toggleDensity(ws, ev.currentTarget as HTMLElement)}></button>
      <button class="wsmini wsidchip" aria-haspopup="menu" title="signed in as ${AUTH.user?.email || 'unknown'} — account menu" @click=${(ev: MouseEvent) => { ev.stopPropagation(); identityMenu(ws, ev.currentTarget as HTMLElement); }}>${AUTH.user?.picture_url ? html`<img class="wsavatar" src="${AUTH.user.picture_url}" alt="">` : ws.ICON.user}<b>${AUTH.user?.name || ws.author() || '?'}</b><span class="wscaret" aria-hidden="true">▾</span></button>
      ${ws._urlFilter ? html`<button class="wsmini" style="border-color:var(--azure);color:var(--azure)" @click=${() => { ws._urlFilter = null; renderActions(payloadStore.get()); }} title="filter arrived from the portfolio link — click to clear">✕ ${ws._urlFilter.label}</button>` : nothing}
      <label style="font-size:11.5px;color:var(--muted);display:inline-flex;align-items:center;gap:5px;margin-left:auto">
        <input type="checkbox" id="wsMine" aria-label="Show only my tasks" ?checked=${ws._mineOnly} @change=${(ev: Event) => { ws._mineOnly = (ev.target as HTMLInputElement).checked; renderActions(payloadStore.get()); }}> my tasks</label>
      <label style="font-size:11.5px;color:var(--muted);display:inline-flex;align-items:center;gap:5px">
        <input type="checkbox" id="wsProgOnly" aria-label="Show only agreed-program tasks" ?checked=${ws._progOnly} @change=${(ev: Event) => { ws._progOnly = (ev.target as HTMLInputElement).checked; renderActions(payloadStore.get()); }}> agreed program only</label>
    </div>
    ${filterBar(ws)}
    ${selectionBar(ws)}
    <div class="wsboard${ws._compact ? ' compact' : ''}" data-board>
    ${grp('⚠ Regressed — verified fixes that broke', ws.filterProg(regressed), 'regressed')}
    ${grp('Implemented — awaiting verification', ws.filterProg(pending), 'implemented', 'recheck to stamp it verified')}
    ${grp('Waiting on client / platform', ws.filterProg(waiting), 'waiting')}
    ${grp('Open & in progress', ws.filterProg(active), '')}
    ${attested.length ? html`<details><summary style="font-size:12px;color:var(--muted);cursor:pointer;margin:14px 0 8px">Human-attested — vouched by a person, not a probe (${attested.length})</summary>${repeat(ws.filterProg(attested), (t) => t.id, (t) => rowHtml(ws, t, ''))}</details>` : nothing}
    ${closed.length ? html`<details><summary style="font-size:12px;color:var(--muted);cursor:pointer;margin:14px 0 8px">Verified & closed (${closed.length})</summary>${repeat(ws.filterProg(closed), (t) => t.id, (t) => rowHtml(ws, t, ''))}</details>` : nothing}
    </div>
    <div class="caption">The board is derived from research runs (stable identity across re-runs) plus custom tasks. The "agreed program" filter affects this view and client exports only — internal findings are never hidden.</div>`, el);

  // toggleDensity's own dedicated render() target — see the module doc
  // comment (DEVIATION: toggleDensity's targeted micro-update).
  const densityBtn = el.querySelector<HTMLElement>('[data-density]');
  if (densityBtn) render(html`${ws.ICON.density}${ws._compact ? 'Comfortable' : 'Compact'}`, densityBtn);
}

export function toggleDensity(ws: Workspace, btn: HTMLElement): void {
  ws._compact = !ws._compact;
  const board = document.querySelector('[data-board]');
  if (board) board.classList.toggle('compact', ws._compact);
  render(html`${ws.ICON.density}${ws._compact ? 'Comfortable' : 'Compact'}`, btn);
  ws.toast(ws._compact ? 'Compact rows — title + status only' : 'Comfortable rows', '');
}

export function filterProg(ws: Workspace, rows: WsTask[]): WsTask[] {
  let out = ws._progOnly ? rows.filter((t) => t.in_program) : rows;
  if (ws._mineOnly) {
    const me = (ws.author() || '').toLowerCase();
    out = out.filter((t) => (t.assignee || '').toLowerCase() === me);
  }
  const f = ws._urlFilter;
  if (f) {
    if (f.kind === 'overdue') out = out.filter((t) => ws.overdueDays(t) > 0);
    else if (f.kind === 'stalled') out = out.filter((t) =>
      (/^waiting/.test(t.status) && ws.stateAge(t) > 14)
      || (['open', 'in-progress'].includes(t.status) && ws.stateAge(t) > 21));
    else if (f.kind === 'assignee') out = out.filter((t) =>
      (t.assignee || '').toLowerCase() === (f.value || '').toLowerCase());
  }
  return out;
}

export function rowHtml(ws: Workspace, t: WsTask, cls: string): TemplateResult {
  const age = ws.stateAge(t);
  const known = t.lastEvent && /known — still flagged/.test(t.lastEvent.body || '');
  const probe = ws.probeFor(t);
  return html`
  <div class="wsrow ${cls}${ws._selected.has(t.id) ? ' selected' : ''}" data-tid="${t.id}">
    <div class="top">
      <input type="checkbox" class="wssel" aria-label="select task for bulk action"
        .checked=${ws._selected.has(t.id)}
        @click=${(ev: MouseEvent) => ev.stopPropagation()}
        @change=${(ev: Event) => ws.toggleSelect(t.id, (ev.target as HTMLInputElement).checked)}>
      <div class="t td-open" role="button" tabindex="0" aria-label="Open task detail: ${t.title}"
        @click=${(ev: MouseEvent) => actClick(ws, ev, t.id, 'open')}
        @keydown=${(ev: KeyboardEvent) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); actClick(ws, ev as unknown as MouseEvent, t.id, 'open'); } }}><b>${t.title}</b>${known ? html` <span class="pill-meta" title="the research run re-flagged this, but the team already knows — status kept">known</span>` : nothing}
        <div class="d">${t.detail || ''}</div>${labelChips(t)}</div>
      <button class="wsst s-${t.status}" data-act="status" aria-haspopup="true" title="${t.status === 'attested' ? `Human-attested by ${t.attested_by || '?'} on ${(t.attested_at || '').slice(0, 10)}${t.attested_note ? ` — ${t.attested_note}` : ''} · a recheck or the next run still overrides this` : `${ws.STATUS_FULL[t.status]||t.status} — click for legal moves; Verified requires evidence`}" @click=${(ev: MouseEvent) => actClick(ws, ev, t.id, 'status')}>${t.status === 'attested' ? ws.ICON.user : nothing}${ws.STATUS_LABEL[t.status] || t.status}</button>
    </div>
    <div class="meta">
      <button class="prio ${t.priority}" data-act="prio" style="border:none;cursor:pointer" aria-label="priority: ${t.priority} — activate to cycle" title="click to cycle priority" @click=${(ev: MouseEvent) => actClick(ws, ev, t.id, 'prio')}>${cap(t.priority)}</button>
      <span>${t.category || ''}</span>
      <span>· ${t.effort || ''}</span>
      ${checklistBadge(t)}
      ${age > 0 && /^waiting|^open$|in-progress|implemented/.test(t.status) ? html`<span class="wsage ${age>14?'hot':''}">${age}d in state</span>` : nothing}
      ${ws.overdueDays(t) > 0 ? html`<span class="wsage late">⚠ ${ws.overdueDays(t)}d late</span>` : nothing}
      <button class="wsmini" data-act="assign" @click=${(ev: MouseEvent) => actClick(ws, ev, t.id, 'assign')}>${ws.ICON.user}${t.assignee ? t.assignee : 'assign'}</button>
      <button class="wsmini" data-act="due" @click=${(ev: MouseEvent) => actClick(ws, ev, t.id, 'due')}>${ws.ICON.cal}${t.due_date ? t.due_date : 'due'}</button>
      <button class="wsmini" data-act="program" title="part of the agreed client program" @click=${(ev: MouseEvent) => actClick(ws, ev, t.id, 'program')}>${t.in_program ? ws.ICON.starFill : ws.ICON.star}program</button>
      ${probe ? html`<button class="wsmini" data-act="recheck" title="${probe.hint}" @click=${(ev: MouseEvent) => actClick(ws, ev, t.id, 'recheck')}>${ws.ICON.recheck}Recheck</button>` : nothing}
      ${t.lastRecheck ? html`<span style="font-size:10.5px;color:var(--muted)" title="latest verification probe">last check: ${t.lastRecheck.verdict === 'observed' ? '✓' : t.lastRecheck.verdict === 'not-observed' ? '✗' : '?'} ${(t.lastRecheck.at||'').slice(5,16).replace('T',' ')}</span>` : nothing}
      ${t.action_key.startsWith('threat:search') ? html`<a class="wsmini" style="text-decoration:none" href="#s1">📈 SERP history</a>` : nothing}
      <button class="wsmini" data-act="expand" aria-expanded="false" style="margin-left:auto" @click=${(ev: MouseEvent) => actClick(ws, ev, t.id, 'expand')}>${t.eventCount || 0} events · ${t.noteCount || 0} notes</button>
    </div>
    <div class="wsexp hidden" data-exp></div>
  </div>`;
}

/* Applies a newly-chosen capacity: persists it (localStorage 'fb-capacity',
 * lib/personas.ts) and re-routes to that capacity's landing — the same
 * PERSONA_ROUTES seam boot() consults on a fresh sign-in, just re-run for the
 * capacity the AM just switched TO. No-op landing (shouldn't happen for a
 * capacity the user actually holds, since every entry in heldCapacities() has
 * a PERSONA_ROUTES mapping today) leaves the page where it is. */
function switchCapacity(u: AuthUser, capacity: string): void {
  setActiveCapacity(capacity);
  const landing = resolveLanding(u);
  if (!landing) return;
  if (landing.startsWith('/')) { location.href = landing; return; }
  location.hash = landing;
}

/* Identity chip menu (design review #2). The chip used to BE the sign-out
 * action — one click, no confirm, full reload (losing filters/scroll/drawer/
 * note drafts). It now opens this small menu: identity display up top, an
 * explicit "Sign out" the AM has to choose. Offline (no AUTH.user — service
 * unreachable) there's no session to end, so no sign-out is offered. Same
 * detached-host + outside-click + Escape wiring as statusMenu, through the
 * shared ws._menu/_menuOut/closeMenu infrastructure.
 *
 * Persona-foundation Task 3: a multi-hat user (≥2 held capacities) also gets
 * a capacity switcher between the identity block and Sign out — a single-hat
 * user (the common case today: 0 or 1 capacity) sees nothing extra, so this
 * is a strict addition, not a redesign, for everyone else.
 *
 * Persona-foundation Task 4: an admin (`AUTH.user.role === 'admin'`) also
 * gets a "Users…" item, below the capacity switcher and above Sign out,
 * opening the admin persona-assignment panel (ws/adminUsers.ts's
 * openUsersPanel — there is no pre-existing admin user-management surface
 * in the hub; this menu item is its only entry point). A non-admin user
 * (or nobody signed in) never sees it. */
export function identityMenu(ws: Workspace, btn: HTMLElement): void {
  ws.closeMenu();
  const u = AUTH.user;
  const caps = u ? heldCapacities(u) : [];
  const current = u ? activeCapacity(u) : null;
  const m = document.createElement('div');
  m.className = 'wsmenu wsidmenu';
  m.setAttribute('role', 'menu');
  render(html`
    <div class="wsidhead">
      ${u?.picture_url ? html`<img class="wsidavatar" src="${u.picture_url}" alt="">` : nothing}
      <div class="wsidwho">
        <b>${u?.name || ws.author() || 'Not signed in'}</b>
        <span class="wsidemail">${u?.email || (u ? '' : 'working offline — reconnect to sign out')}</span>
      </div>
    </div>
    ${u && caps.length > 1 ? html`
    <div class="wsidcaps" role="group" aria-label="switch working capacity">
      <div class="wsidcapslabel">Working as</div>
      ${caps.map((c) => html`
        <button role="menuitem" class="wsidcap${c === current ? ' on' : ''}" aria-current=${c === current ? 'true' : 'false'}
          @click=${() => { ws.closeMenu(); switchCapacity(u, c); }}>${capacityLabel(c)}${c === current ? html`<span aria-hidden="true"> ✓</span>` : nothing}</button>`)}
    </div>` : nothing}
    ${u && u.role === 'admin' ? html`<button role="menuitem" data-users @click=${() => { ws.closeMenu(); openUsersPanel(ws); }}>Users…</button>` : nothing}
    ${u ? html`<button role="menuitem" data-signout @click=${() => { ws.closeMenu(); ws.switchAuthor(); }}>Sign out</button>` : nothing}`, m);
  document.body.appendChild(m);
  const r = btn.getBoundingClientRect();
  m.style.left = Math.min(r.left, innerWidth - 240) + 'px';
  // Flip the menu ABOVE its button when there isn't room below (review #8): the
  // CRM rail-foot identity button sits at the viewport bottom, so a
  // below-anchored menu put Sign out off-screen. A no-op when room exists (every
  // top-of-viewport toolbar menu keeps opening downward). offsetHeight is
  // readable now that the menu is in the DOM.
  const mh = m.offsetHeight;
  const roomBelow = r.bottom + 4 + mh <= innerHeight;
  m.style.top = (roomBelow ? r.bottom + scrollY + 4 : Math.max(scrollY + 4, r.top + scrollY - mh - 4)) + 'px';
  ws._menu = m; ws._menuOwner = btn;
  const first = m.querySelector<HTMLButtonElement>('button[role="menuitem"]');
  if (first) first.focus();
  m.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); ws.closeMenu(true); } });
  ws._menuOut = (ev: MouseEvent) => { if (!m.contains(ev.target as Node) && ev.target !== btn) ws.closeMenu(); };
  setTimeout(() => document.addEventListener('click', ws._menuOut!), 0);
}

/* Task-Management Phase 2 / K: the segmented List | Board view toggle, in the
   board toolbar. Persisted per-user in localStorage via ws.setView (no server
   round-trip — a view preference, not shared task state). Rendered in BOTH
   views so the AM can switch back. Tokens only. */
export function viewToggle(ws: Workspace): TemplateResult {
  // reuses the existing .seg segmented control (app.css) — no new pill/toggle
  // vocabulary; text-only so it needs zero icon-alignment CSS.
  const seg = (v: 'list' | 'board', label: string) => html`
    <button class=${ws._view === v ? 'on' : ''} role="tab" aria-selected=${ws._view === v ? 'true' : 'false'}
      title="${label} view" @click=${() => ws.setView(v)}>${label}</button>`;
  return html`<div class="seg wsviewtoggle" role="tablist" aria-label="board view">${seg('list', 'List')}${seg('board', 'Board')}</div>`;
}

/* Task-Management Phase 2 / K (Design gate B3): the ONE shared optimistic-paint
   + Undo + PATCH + rollback path for a single LEGAL status transition, factored
   out of statusMenu so the kanban drag and the list menu share it verbatim — no
   duplicated transition logic, identical evidence-gate/stall-clock guarantees
   (all server-side) and identical optimistic/rollback UX in both views.

   The CALLER owns the legality check and (for `wont-do`) collecting the required
   reason BEFORE calling: this assumes `to` is legal for `t.status` and, when
   `to === 'wont-do'`, that `reason` is non-empty. A same-lane call is a no-op
   (no PATCH) — the stall clock can never move on a drop onto a card's own lane.
   `author` is NOT sent (session authorship; the server 422s the field). */
export async function applyTransition(
  ws: Workspace, t: WsTask, to: string, reason: string | null = null,
): Promise<void> {
  const from = t.status;
  if (to === from) return; // same-lane no-op — never a PATCH (stall clock safe)
  const author = await ws.ensureAuthor();
  // optimistic paint: the legal-moves check already ran client-side, so show the
  // new status at once; a failed write rolls back and corrects the toast.
  // Verified/Regressed still only ever arrive here when the SERVER put them
  // (reconcile/recheck) — never from a bare click/drag.
  t.status = to;
  ws.repaintAll();
  ws.toast(`${t.title.slice(0, 50)} → ${ws.STATUS_FULL[to] || to} · by ${author}`, 'ok',
    { label: 'Undo', fn: async () => {
        t.status = from; ws.repaintAll();
        try {
          await ws.api(`/tasks/${t.id}`, { method: 'PATCH', body: JSON.stringify({ status: from, expected_status: to }) });
        } catch (e) { ws.toast('Could not undo on the server — refreshing.', 'warn'); }
        ws.refresh();
      } });
  try {
    await ws.api(`/tasks/${t.id}`, { method: 'PATCH', body: JSON.stringify({ status: to, expected_status: from, reason }) });
    ws.refresh(); // quietly pick up server-side effects (event counts, reconcile)
  } catch (e) {
    t.status = from; ws.repaintAll(); // roll back the optimistic paint
    const err = e as { status?: number; message?: string };
    ws.toast(err.status === 409 ? 'Someone else changed this task — board refreshed.' : 'Update failed — rolled back: ' + err.message, 'warn');
    ws.refresh();
  }
}

export function statusMenu(ws: Workspace, btn: HTMLElement, t: WsTask): void {
  ws.closeMenu();
  const legal = ws.transitions[t.status] || [];
  const m = document.createElement('div'); m.className = 'wsmenu'; m.setAttribute('role', 'menu');
  // body-building converts to lit render() into this detached host (per the
  // brief's requirement 2); the click/keydown/focus wiring immediately
  // below stays imperative post-render code — same carve-out as
  // views/timeline.ts's renderImpCanvas (async status-transition logic with
  // optimistic-paint/undo/arrow-key nav across sibling items is a genuine
  // multi-node concern, not a single element's own @click).
  render(html`${legal.map((s) => html`<button role="menuitem" tabindex="-1" data-to="${s}">${ws.STATUS_FULL[s] || s}</button>`)}
    <div class="note">${t.status === 'implemented'
        ? 'Verified is stamped by a recheck or the next run — not by hand.'
        : 'Verified/Regressed come from evidence (recheck or run), not clicks.'}</div>`, m);
  document.body.appendChild(m);
  const r = btn.getBoundingClientRect();
  m.style.left = Math.min(r.left, innerWidth - 240) + 'px';
  // Flip the menu ABOVE its button when there isn't room below (review #8): the
  // CRM rail-foot identity button sits at the viewport bottom, so a
  // below-anchored menu put Sign out off-screen. A no-op when room exists (every
  // top-of-viewport toolbar menu keeps opening downward). offsetHeight is
  // readable now that the menu is in the DOM.
  const mh = m.offsetHeight;
  const roomBelow = r.bottom + 4 + mh <= innerHeight;
  m.style.top = (roomBelow ? r.bottom + scrollY + 4 : Math.max(scrollY + 4, r.top + scrollY - mh - 4)) + 'px';
  ws._menu = m; ws._menuOwner = btn;
  const items = [...m.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]')];
  items.forEach((x, i) => {
    x.onclick = async () => {
      const to = x.dataset.to!;
      let reason: string | null = null;
      if (to === 'wont-do') {
        ws.closeMenu();
        reason = await ws.ask({ title: "Why won't this be done?", multiline: true,
          hint: 'Required — kept on the task record so the next run marks it known instead of re-flagging it.', ok: "Won't do" });
        if (!reason) return;
      } else if (to === 'attested') {
        // Q-03 Design condition 2: attesting REQUIRES a non-empty evidence note.
        // Enforced client-side here (empty/whitespace = no change) AND server-side
        // (transition_task). The note rides the same PATCH path as wont-do's reason.
        ws.closeMenu();
        reason = await ws.ask({ title: 'Mark human-attested', multiline: true,
          hint: 'Required — describe the outside evidence (e.g. "client emailed a screenshot confirming the fix"). This is recorded as a human attestation, kept distinct from a probe-verified result; a later recheck or run still overrides it either way.',
          ok: 'Attest' });
        if (!reason || !reason.trim()) return;
      } else ws.closeMenu(true);
      // the optimistic-paint/Undo/PATCH/rollback path is now the shared
      // applyTransition helper (Design gate B3) — the kanban drag uses the
      // SAME one, so the list menu and the board drag can never diverge.
      await applyTransition(ws, t, to, reason);
    };
    x.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); (items[i+1]||items[0])?.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (items[i-1]||items[items.length-1])?.focus(); }
      else if (e.key === 'Escape') { e.preventDefault(); ws.closeMenu(true); }
    });
  });
  if (items[0]) items[0].focus();
  ws._menuOut = (ev: MouseEvent) => { if (!m.contains(ev.target as Node) && ev.target !== btn) ws.closeMenu(); };
  setTimeout(() => document.addEventListener('click', ws._menuOut!), 0);
}
